import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Zap } from 'lucide-react';
import { cn } from '../../lib/utils';
import { dataManager } from '../../utils/dataManager';
import { useData } from '../../hooks/useData';

export const GlobalTicker: React.FC = () => {
  const location = useLocation();
  const isTv = location.pathname === '/tv';
  const matches = useData(dataManager.getAllMatches);
  const tourneys = useData(dataManager.getTournaments);
  const msg = useData(dataManager.getCustomTickerMessage);
  const theme = useData(dataManager.getTheme);
  const isLight = theme === 'light';

  const items = useMemo(() => {
    const today = new Date().toDateString();
    
    // Filter matches to only show those from today AND exclude 0-0 scores
    // Request update: Hide Walkovers and Bye matches.
    const rec = matches
      .filter(m => {
        const isToday = new Date(m.timestamp).toDateString() === today;
        // Must have actual score activity
        const hasScore = m.s1 > 0 || m.s2 > 0;
        // Check for BYE players
        const isBye = m.p1 === 'BYE' || m.p2 === 'BYE' || m.p1Name === 'BYE' || m.p2Name === 'BYE';
        
        return isToday && hasScore && !m.isWalkover && !isBye;
      })
      .sort((a,b) => b.timestamp - a.timestamp)
      .map(m => {
        const w = dataManager.getPlayer(m.winner || "")?.name || "Unk";
        const l = dataManager.getPlayer(m.winner === m.p1 ? m.p2 : m.p1)?.name || "Unk";
        return { t: `${w} def. ${l} (${m.s1}-${m.s2})`, icon: 'res' };
      });

    const lastT = [...tourneys].reverse().find(t => t.winnerId);
    if (lastT) rec.unshift({ t: `DEFENDING CHAMPION: ${dataManager.getPlayer(lastT.winnerId!)?.name}, ${lastT.name}`, icon: 'news' });
    if (msg) rec.push({ t: msg, icon: 'msg' });
    
    return rec;
  }, [matches, tourneys, msg]);

  if (!items.length) return null;

  return (
    <div className={cn(
      "z-[60] border-y md:border-t md:border-b-0 fixed left-0 right-0 w-full backdrop-blur-2xl transition-colors duration-300", 
      isTv 
        ? "bottom-0 h-20 bg-black border-white/20" 
        : cn(
            // Mobile: Top (0), Desktop: Bottom (0)
            "top-0 md:top-auto md:bottom-0 h-10",
            isLight 
              ? "bg-white/95 border-kfppc-royal/20 shadow-lg" 
              : "bg-kfppc-surface/99 border-white/10"
          )
    )}>
      <div className="flex items-center h-full overflow-hidden relative">
        <div className={cn(
          "absolute left-0 h-full z-20 flex items-center px-4 font-black italic shadow-xl transition-colors duration-300", 
          isTv ? "w-40 text-2xl bg-kfppc-royal text-white" : cn(
            "w-24 text-xs",
            isLight ? "bg-kfppc-royal text-white" : "bg-kfppc-royal text-white"
          )
        )}>
          LIVE
        </div>
        <div className={cn(
            "flex whitespace-nowrap animate-marquee items-center gap-8",
            isTv ? "pl-40" : "pl-28 md:pl-40"
        )}>
          {[...items, ...items].map((x, i) => (
            <div key={i} className={cn(
              "flex items-center gap-2 font-bold uppercase italic tracking-wide transition-colors",
              isLight ? "text-kfppc-royal" : "text-kfppc-white"
            )}>
              <Zap size={14} className="text-kfppc-gold" /> 
              {x.t}
            </div>
          ))}
        </div>
      </div>
      <style>{`.animate-marquee { animation: marquee 45s linear infinite; } @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }`}</style>
    </div>
  );
};